/**
 * Created by Vinicius
 */

import {
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { Subject } from 'rxjs';
import { last, map, takeUntil } from 'rxjs/operators';
import { ImageCroppedEvent, base64ToFile } from 'ngx-image-cropper';
import { SettingsData } from 'app/@core/interfaces/common/settings';
import { UserStore } from 'app/@core/stores/user.store';
import { COLORS, ColorSetting } from 'app/@theme/styles/colors';
import { replaceRootStyle } from 'app/@core/utils/themes';
import { PremiumModalComponent } from '../premium-modal/premium-modal.component';
import { JSONStringify } from 'app/SupportOps';
import { AppSettingsService } from 'app/@core/backend/common/services/appSettings.service';
import { AppSettings } from 'app/@core/interfaces/common/appSettings';
import { AppSettingsConfigurations } from 'app/@components/app-settings/app-settings.component';
import { User } from 'app/@core/interfaces/common/users';
import { TfAdminLoginService } from 'app/@core/backend/common/services/tfAdminLogin.service';
import { UsersService } from 'app/@core/backend/common/services/users.service';

@Component({
  selector: 'ngx-brand',
  templateUrl: './brand.component.html',
  styleUrls: ['./brand.component.scss'],
})
export class BrandComponent implements OnDestroy, OnInit {
  @ViewChild('cropperDlg', { static: true })
  cropperDlg?: TemplateRef<HTMLTemplateElement>;
  @ViewChild('cropperDlgIcon', { static: true })
  cropperDlgIcon?: TemplateRef<HTMLTemplateElement>;
  @ViewChild('upgradeDlg', { static: true })
  upgradeDlg?: TemplateRef<HTMLTemplateElement>;
  @ViewChild('iframe', { static: true }) iframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild('file', { static: true }) fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileAppLogo', { static: true }) fileInputAppLogo?: ElementRef<HTMLInputElement>;
  @ViewChild('fileAppSplashScreenLogo', { static: true }) fileInputAppSplashScreenLogo?: ElementRef<HTMLInputElement>;


  isSaving: boolean = false;
  logoSrc: string;
  logoEditSrc: string;
  imageChangedEvent: any = '';
  appLogoSrc: string;
  appLogoEditSrc: string;
  appLogoChangedEvent: any = '';
  appName: string;
  color: ColorSetting = COLORS[0];
  splashColor: ColorSetting = COLORS[0];
  isIframeLoaded: boolean = false;
  changed: boolean = false;
  public configurations: AppSettingsConfigurations;
  public appSettings: AppSettings;
  public user: User;
  public isTfAdmin: boolean = false;

  private readonly unsubscribe$ = new Subject<void>();
  private fileName: string;
  private iframeDoc: Document;
  public userSettings: any;

  constructor(
    private settingsService: SettingsData,
    private userStore: UserStore,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService,
    private appSettingService: AppSettingsService,
    private tfAdminLoginService: TfAdminLoginService,
    private usersService: UsersService,
  ) {
    if (userStore.settings(true)) this.loadBrand();
    else
      userStore
        .onUserStateChange()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => this.loadBrand());
  }
  ngOnInit(): void {
    this.isTfAdmin = this.tfAdminLoginService.isLoggedIn;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public get appStoreUrl(): string {
    return this.userStore.settings(true)?.appStoreUrl;
  }

  public get playStoreUrl(): string {
    return this.userStore.settings(true)?.playStoreUrl;
  }

  public get isCustomAppEnabled(): boolean {
    return this.userStore.customAppEnabled() || true;
  }

  private loadBrand(): void {
    this.user = this.userStore.getUser();
    this.logoSrc = this.userStore.settings(true)?.logo;
    this.color = this.userStore.settings(true)?.color ?? COLORS[0];
    this.splashColor = this.userStore.settings(true)?.appColor ?? COLORS[0];
    this.appLogoSrc = this.userStore.settings(true)?.appLogo;
    this.appName = this.userStore.settings(true)?.appName;
    this.userSettings = this.userStore.settings(true);
    console.log("Loading brand...");
    if(this.user) {
    this.appSettingService.get(this.user.id).subscribe(res => {
      console.log('configurations',res);
      if(res) {
        this.appSettings = res;
        if(res.configuration)
        this.configurations = JSON.parse(res.configuration);
      }
    });
  }
  }

  public get isCustomAppInProduction(): boolean {
    return this.userStore.isCustomAppInProduction();
  }

  public get customAppEnabled(): boolean {
    return this.userStore.customAppEnabled();
  }

  onIframeLoaded(): void {
    this.iframeDoc =
      this.iframe.nativeElement.contentDocument ||
      this.iframe.nativeElement.contentWindow.document;
    this.isIframeLoaded = true;
    this.previewLogo();
    replaceRootStyle(this.iframeDoc, this.color);
  }

  onLogoChange(event): void {
    const files = event.target.files;
    if (!files?.length) return;

    this.fileName = files[0].name;
    this.imageChangedEvent = event;

    this.dialogService.open(this.cropperDlg, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
    });
  }

  onAppLogoChange(event): void {
    const files = event.target.files;
    if (!files?.length) return;

    this.appLogoChangedEvent = event;

    this.dialogService.open(this.cropperDlgIcon, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
    });
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.logoEditSrc = event.base64;
    this.fileInput.nativeElement.value = '';
    this.changed = true;
  }

  onImageIconCropped(event: ImageCroppedEvent): void {
    this.appLogoEditSrc = event.base64;
    this.fileInputAppLogo.nativeElement.value = '';
    this.changed = true;
  }

  onLoadImageFailed(event): void {
    this.toastrService.danger('', 'Loading image failed.');
    this.fileInput.nativeElement.value = '';
  }

  onLoadImageIconFailed(event): void {
    this.toastrService.danger('', 'Loading image failed.');
    this.fileInputAppLogo.nativeElement.value = '';
  }

  onCancelImageCropping(ref: NbDialogRef<HTMLTemplateElement>): void {
    ref.close();
    this.fileInput.nativeElement.value = '';
  }

  onCancelImageIconCropping(ref: NbDialogRef<HTMLTemplateElement>): void {
    ref.close();
    this.fileInputAppLogo.nativeElement.value = '';
  }

  onUpdateLogo(ref: NbDialogRef<HTMLTemplateElement>): void {
    this.logoSrc = this.logoEditSrc;
    ref.close();
    this.previewLogo();
  }

  onUpdateLogoIcon(ref: NbDialogRef<HTMLTemplateElement>): void {
    this.appLogoSrc = this.appLogoEditSrc;
    ref.close();
    this.previewAppLogo();
  }

  // change logo on iframe
  private previewLogo(): void {
    if (!this.logoSrc?.length) return;
    this.iframeDoc
      .querySelector('img.image-logo')
      .setAttribute('src', this.logoSrc);
  }

  private previewAppLogo(): void {
    if (!this.appLogoSrc?.length) return;
    this.iframeDoc
      .querySelector('img.logo')
      .setAttribute('src', this.appLogoSrc);
  }

  onColorChanged(color: ColorSetting): void {
    this.color = color;
    console.log(this.color);

    this.changed = true;
    replaceRootStyle(this.iframeDoc, color);
  }

  onSplashColorChanged(color: ColorSetting): void {
    this.splashColor = color;
    this.changed = true;
  }

  onSave(): void {
    if (this.userStore.isPayingUser()) {
      this.onSaveAnyway(false);
      return;
    }

    this.onShowPremiumDialog();
  }

  onShowPremiumDialog() {
    const ref: ComponentRef<PremiumModalComponent> =
      this.dialogService.open(PremiumModalComponent, {
        closeOnBackdropClick: false,
        closeOnEsc: false,
        autoFocus: false,
        context: {
          CloseCallBack: this.onSaveAnyway.bind(this, false),
          OnPremiumCallBack: this.onSaveAnyway.bind(this, true),
        },
      }).componentRef;
  }

  saveAppSettings({user, settings}): void {
    // const userData: User = {...this.user,
    //   isCustomAppInProduction: user.isCustomAppInProduction,
    //    customAppEnabled: user.customAppEnabled};
    //    console.log(userData);
    this.usersService.updateCurrent(
      {
        id: user.id ? user.id : '',
        role: user.role ? user.role : '',
        firstName: user.firstName ? user.firstName : '',
        lastName: user.lastName ? user.lastName : '',
        phoneNumber: user.phoneNumber ? user.phoneNumber : '',
        businessName: user.businessName ? user.businessName : '',
        email: user.email ? user.email : '',
        conversionFactorsPreference: user.conversionFactorsPreference? user.conversionFactorsPreference: 'g',
        address: {
          street:
            user.address && user.address.street ? user.address.street : '',
          secondAddress:
            user.address && user.address.secondAddress
              ? user.address.secondAddress
              : '',
          city: user.address && user.address.city ? user.address.city : '',
          country:
            user.address && user.address.country ? user.address.country : '',
          zipCode:
            user.address && user.address.zipCode ? user.address.zipCode : '',
        },
        isCustomAppInProduction: user.isCustomAppInProduction,
        customAppEnabled: user.customAppEnabled,
        gHLApiKey: user.gHLApiKey && user.gHLApiKey.length > 1? user.gHLApiKey: null,
      }
    ).subscribe(
      (result: User) => {
        this.userStore.setUser(user);
        this.toastrService.success('', 'User app settings is updated.');
      },
      () => {
        this.toastrService.danger('', 'User updating failed.');
      }
    );
  }


  private onSaveAnyway(isRedirectingToSubscriptions): void {

    this.isSaving = true;
    let logo;
    if (this.logoEditSrc)
      logo = new File([base64ToFile(this.logoEditSrc)], this.fileName, {
        type: 'image/png',
      });
    else logo = this.logoSrc;
    let appLogo;
    if (this.appLogoEditSrc)
      appLogo = new File([base64ToFile(this.appLogoEditSrc)], this.fileName, {
        type: 'image/png',
      });
    else appLogo = this.appLogoSrc;
    this.settingsService
      .updateCurrent({
        themeName: this.userStore.settings(true)?.themeName || 'default',
        logo,
        appLogo,
        appColor: this.splashColor,
        color: this.color,
        appName: this.appName,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        last(),
        map((res) => res.body),
      )
      .subscribe(
        (settings) => {
          this.logoSrc =
            this.logoEditSrc?.length > 0 ? settings.logo : this.logoSrc;
          this.logoEditSrc = null;
          this.userStore.setSettings(settings);
          if (!isRedirectingToSubscriptions) this.toastrService.success('', 'Branding config is updated.');
          this.isSaving = false;
        },
        () => {
          if (!isRedirectingToSubscriptions) this.toastrService.danger('', 'Branding config updating failed.');
          this.isSaving = false;
        },
      );
  }

}
